/**
 * The three pieces of `bookingsWidget.vue` that decide what a cell says and how it is
 * coloured, extracted so each boundary can be asserted without rendering a grid.
 *
 * Ported from `getCellValue`, `getCellClass` and the `capacity` computed. The legacy versions
 * take `(index, key, bookings, capacity)` and index into the arrays themselves; these take the
 * one column and the one capacity they need, which is the same logic with the lookups lifted
 * to the caller.
 */

/** One column of the grid: `rowKey -> booked count`. `bookings[columnIndex]` in the payload. */
export type BookingsColumn = Record<string, number>;

/** One configured row. `key` indexes the payload; `value` is the label on screen. */
export type BookingsRow = {
  key: string;
  value: string;
  /** Overrides the payload's capacity for this row. */
  capacity?: number;
};

/** Which `options.colors` block a cell takes its text and background from. */
export type CellKind = 'great' | 'good' | 'poor' | 'bad' | 'disabled';

/**
 * `payload.capacity`, then any `row.capacity` from config on top. **Config wins.**
 *
 * Derived rather than mutated: the legacy computed assigns `res = data.value.capacity` and then
 * writes into `res`, which edits the stored payload object in place — so after one render the
 * broker's capacities are permanently overwritten by the config's, and a later message that
 * *omits* `capacity` inherits the config values through an object that is no longer the
 * broker's. Same visible result, no shared mutation.
 *
 * `if (row.capacity)` is a truthiness test in the legacy code and stays one: a configured `0`
 * falls through to the payload rather than pinning the row at zero capacity.
 */
export function resolveCapacity(
  rows: readonly BookingsRow[],
  payloadCapacity: Record<string, number> | undefined,
): Record<string, number> {
  const capacity = { ...payloadCapacity };
  for (const row of rows) {
    if (row.capacity) capacity[row.key] = row.capacity;
  }
  return capacity;
}

/**
 * `` `${booked}/${capacity}` `` — `0` for a missing count, `-` for a missing capacity.
 *
 * The `0/-` this returns with no data at all is the widget's empty state
 * (docs/MIGRATION-WIDGETS.md §9).
 */
export function cellValue(
  column: BookingsColumn | undefined,
  key: string,
  capacity: number | undefined,
): string {
  const booked = column?.[key] ?? 0;
  const cap = capacity ?? '-';
  return `${String(booked)}/${String(cap)}`;
}

/**
 * Occupancy band for one cell: `<50%` great, `<70%` good, `<90%` poor, otherwise bad.
 *
 * Two edges are legacy behaviour rather than an oversight, and both are load-bearing:
 *
 * 1. **No column or no capacity is `disabled`** — that is the "this feed has not arrived"
 *    colour, and it is what the whole grid shows before the first message.
 * 2. **A row key missing from a column that *is* present counts as zero bookings**, so the
 *    cell reads `0/28` in the *great* colour rather than being disabled. It is the same
 *    default `getCellValue` applies to the number it prints, so the value and the colour agree.
 *    (SYM-501's brief summarises this case as `disabled`; the legacy code does not, and what is
 *    on the wall today is green.)
 *
 * A capacity of `0` yields `Infinity` or `NaN`, both of which fall through to `bad` — again
 * exactly what the legacy `switch (true)` did.
 */
export function cellKind(
  column: BookingsColumn | undefined,
  key: string,
  capacity: number | undefined,
): CellKind {
  if (column === undefined || capacity === undefined) return 'disabled';

  const percentage = ((column[key] ?? 0) / capacity) * 100;
  if (percentage < 50) return 'great';
  if (percentage < 70) return 'good';
  if (percentage < 90) return 'poor';
  return 'bad';
}
